/*
 * Which *place* the reader is in, and what belongs to it (UX §1.2).
 *
 * A place is a top-level destination of the rail — Home, Activity, Search, Administration — and it
 * is derived from the pathname rather than registered anywhere: a route is added by a module at
 * runtime, so a table of routes here would be a second list to keep in step with the first. The
 * conversation routes are Home, because a conversation is what Home shows.
 *
 * The sidebar is on every place (VISUAL.md §7.13, D15): a section that names no place is drawn
 * beside Activity, Search, the directory, Settings, Administration and the 404 exactly as beside a
 * conversation, so that moving between a conversation and a page never shifts the interface
 * sideways by a column. A section that does name a place is drawn there alone.
 *
 * The one exception is a place that *claims* the column (`SidebarSection.sidebar`): there the
 * placeless sections are left out, because the place has a column of its own to say something with
 * — AI Chats' find field, the chats waiting for the reader and their own chats. The claim is made by the section
 * that names the place, so no module learns of another and no other place changes.
 *
 * A place may also *fold* its column (`SidebarSection.fold`, G-02 §2): on the addresses it names and
 * below the width it names — AI Chats folds its list beside an open chat under 1600 px —
 * the column is not drawn, so that the page keeps its width, and the page's toggle brings it back
 * *over* the page: an overlay that goes on Escape, on a click elsewhere and on the next navigation,
 * and that never moves a line of the text. The look offers to keep the list (the shell's own
 * button): pressed, the column stands beside the page at any width, and that choice is remembered
 * like the toggle's — a sidebar that stays, for the person who moves between pages all
 * day, at the cost of the text's width, which is theirs to spend. The decision is two pure
 * functions here (`restingColumn`, `toggledColumn`) and one hook (`useSidebarFold`) that the page's
 * toggle reads; the shell holds the state and draws it.
 */

import { useRouterState } from "@tanstack/react-router"
import { createContext, useContext } from "react"
import { readValue, storageKey, writeValue } from "../../lib/storage.ts"
import type { SidebarFoldRule, SidebarSection } from "../modules/registry.ts"
import { useSlot } from "../modules/slots.ts"
import { SIDEBAR_ID } from "./layout.ts"

/** The place of "/" and of every conversation: the one the rail's first row opens. */
export const HOME_PLACE = "home"

/** The pathname prefixes that are Home. A conversation is not a place of its own. */
const HOME_PREFIXES = ["/c/", "/d/"]

/**
 * The place of a pathname. Everything unlisted is its own first segment, so a new area is a place
 * the day its route exists and needs no entry here; only the routes that are *not* their own place
 * — the conversations — are named.
 */
export function placeOf(pathname: string): string {
	if (pathname === "/" || HOME_PREFIXES.some((prefix) => pathname.startsWith(prefix))) {
		return HOME_PLACE
	}
	return pathname.split("/")[1] ?? HOME_PLACE
}

/** The current place, following the router. */
export function usePlace(): string {
	return useRouterState({ select: (state) => placeOf(state.location.pathname) })
}

/**
 * The sections drawn on one place, in their own order: every section that names no place, and the
 * ones that name this one — unless a section of this place has claimed the column, and then its
 * own place's sections alone.
 */
export function sectionsInPlace(
	sections: readonly SidebarSection[],
	place: string,
): SidebarSection[] {
	const claimed = sections.some((section) => section.place === place && section.sidebar === "own")
	return sections
		.filter((section) =>
			claimed ? section.place === place : section.place === undefined || section.place === place,
		)
		.sort((a, b) => a.order - b.order)
}

/**
 * The sidebar sections of the place the reader is in. The shell asks it whether there is a column
 * at all, the sidebar asks it what to draw, and neither has to know the other's answer.
 */
export function useSidebarSections(): SidebarSection[] {
	return sectionsInPlace(useSlot("sidebarSections"), usePlace())
}

/**
 * How the column stands. `docked` is a column of the grid, as on every place without a fold;
 * `folded` is no column at all; `overlay` is the folded column brought back over the page, which
 * takes no track of the grid and so moves nothing beside it.
 */
export type ColumnState = "docked" | "folded" | "overlay"

/**
 * What a person last chose for a column that folds: to keep it open, or folded away. The empty
 * string is no choice yet, and the window's width decides.
 */
export type FoldChoice = "open" | "folded" | ""

/** The fold of the sections drawn on a place: the first one that asks for it. */
export function foldRuleOf(sections: readonly SidebarSection[]): SidebarFoldRule | undefined {
	return sections.find((section) => section.fold !== undefined)?.fold
}

/**
 * Where the column rests, before anybody asks for the overlay.
 *
 * Only an address the rule names folds at all — the tab's home and its tree of everything keep the
 * column, because there it is the way in. On such an address the person's own choice wins, at any
 * width: somebody who folded the list on a wide screen does not want it back because the window is
 * wide, and somebody who kept it open does not want it gone because the laptop is small. Without a
 * choice the width decides — `roomy` is whether the window is at least the rule's `belowPx`.
 */
export function restingColumn(
	rule: SidebarFoldRule | undefined,
	pathname: string,
	roomy: boolean,
	choice: FoldChoice,
): "docked" | "folded" {
	if (rule === undefined || !rule.on(pathname)) return "docked"
	if (choice !== "") return choice === "open" ? "docked" : "folded"
	return roomy ? "docked" : "folded"
}

/**
 * What the band's toggle does from each state, and the choice it leaves behind, if any.
 *
 * Hiding a standing column is a choice and is remembered. Showing a folded one is a choice only
 * where the window has room for the column: there it stands again and that is remembered too.
 * Where it has not, showing it is a look at the list — it comes over the page and goes with the next
 * navigation — and a look is not a decision about the next page, so nothing is remembered and the
 * page after it rests as before. Pressed on the overlay, the toggle simply puts it away.
 */
export function toggledColumn(
	state: ColumnState,
	roomy: boolean,
): { next: ColumnState; remember?: Exclude<FoldChoice, ""> } {
	if (state === "docked") return { next: "folded", remember: "folded" }
	if (state === "overlay") return { next: "folded" }
	return roomy ? { next: "docked", remember: "open" } : { next: "overlay" }
}

/**
 * Where a place's choice is kept: on this device, beside the widths the person dragged the panes
 * to. A folded column is the narrowest width a pane can have, and both are the same kind of fact —
 * how this person likes this screen on this machine — which a second device with another window
 * has every reason to answer differently. It is keyed by place, so the key names no module.
 */
function foldKey(place: string): string {
	return storageKey("paneWidths", "fold", place)
}

/** The choice remembered for a place's column; "" when there is none or the browser keeps nothing. */
export function readFoldChoice(place: string): FoldChoice {
	const stored = readValue(foldKey(place))
	return stored === "open" || stored === "folded" ? stored : ""
}

/** Remembers a place's choice; silent when the browser keeps nothing (the choice then lasts the tab). */
export function writeFoldChoice(place: string, choice: FoldChoice): void {
	writeValue(foldKey(place), choice)
}

/** The fold as a toggle sees it, and what it may do with it. */
export interface SidebarFold {
	/**
	 * Whether the column folds here at all: false on every other place, on the addresses the rule
	 * leaves out, below 768 px (where the sidebar is no column) and in a shell with no section. A
	 * page draws no toggle then.
	 */
	available: boolean
	state: ColumnState
	/** Whether the column is on screen — standing or over the page: the toggle's `aria-expanded`. */
	expanded: boolean
	/**
	 * The toggle's press (`toggledColumn`). Where it brings the overlay, the focus goes into the
	 * column and returns to the element that had it when Escape puts the column away.
	 */
	toggle: () => void
	/**
	 * The id of the column's region, for the toggle's `aria-controls`. It is also how the shell
	 * knows the toggle: a press that starts on a control naming this id is not a click outside the
	 * overlay, so the toggle closes the overlay once rather than closing it and opening it again.
	 */
	controls: string
}

const NO_FOLD: SidebarFold = {
	available: false,
	state: "docked",
	expanded: true,
	toggle: () => undefined,
	controls: SIDEBAR_ID,
}

/** Provided by the shell's frame; outside it (a screen under test) nothing folds. */
export const SidebarFoldContext = createContext<SidebarFold>(NO_FOLD)

/**
 * The column's fold, for the control that toggles it — an AI chat's band's first control. A page
 * that is drawn outside the shell, or on a place without a fold, reads `available: false`.
 */
export function useSidebarFold(): SidebarFold {
	return useContext(SidebarFoldContext)
}
